// Hash router: '#/path?query'. Routes per docs/DESIGN.md §3 + §7 (v3):
//   /  overview · /workspace?filter=&client=&q= · /clients?status= · /clients/:clientId[/months/:n][?view=months]
//   /batches/:batchId · /calendar · /performance · /people · /people/:memberId · /settings · anything else → notFound.
// Legacy /workspace?client=…&month=n (the v2.1 client mode) redirects (history replace) to /clients/:id/months/n.
import { useEffect, useState } from 'preact/hooks';
import { batchLabel, monthLabel } from '../shared/constants.js';

// Months run 01–99 (DESIGN §7 — not capped by the contract).
export const MAX_MONTH = 99;

const ROUTES = [
  ['overview', /^\/$/, []],
  ['workspace', /^\/workspace$/, []],
  ['clients', /^\/clients$/, []],
  ['client', /^\/clients\/([^/]+)$/, ['clientId']],
  ['client', /^\/clients\/([^/]+)\/months\/(\d{1,3})$/, ['clientId', 'month']],
  ['batch', /^\/batches\/([^/]+)$/, ['batchId']],
  ['calendar', /^\/calendar$/, []],
  ['performance', /^\/performance$/, []],
  ['people', /^\/people$/, []],
  ['person', /^\/people\/([^/]+)$/, ['memberId']],
  ['settings', /^\/settings$/, []],
];

// [pattern, (match, query) → target path | null (no redirect)]
const REDIRECTS = [
  [/^\/workspace$/, (m, q) => {
    if (!q.client || !q.month) return null;
    const { client, month, filter, ...rest } = q;
    const n = validMonth(month);
    return buildPath(`/clients/${encodeURIComponent(client)}${n ? `/months/${n}` : ''}`, { view: rest.view });
  }],
  [/^\/dashboard$/, (m, q) => buildPath('/', q)],
];

const hasWindow = typeof window !== 'undefined';

function decode(s) {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

function validMonth(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= MAX_MONTH ? n : 0;
}

function splitHash(hash) {
  let raw = String(hash || '').replace(/^#/, '');
  if (!raw.startsWith('/')) raw = `/${raw}`;
  const qi = raw.indexOf('?');
  const path = (qi < 0 ? raw : raw.slice(0, qi)).replace(/\/+$/, '') || '/';
  const query = Object.fromEntries(new URLSearchParams(qi < 0 ? '' : raw.slice(qi + 1)));
  return { path, query };
}

export function buildPath(path, query = {}) {
  const clean = Object.fromEntries(Object.entries(query).filter(([, v]) => v != null && v !== ''));
  const qs = new URLSearchParams(clean).toString();
  return qs ? `${path}?${qs}` : path;
}

// '#/clients/abc/months/4' → { name: 'client', params: { clientId: 'abc', monthNumber: 4 }, query, path }.
// Workspace params: clientId ('' when absent) from ?client=. Client params: clientId + monthNumber (1–99, 0 = none).
// A legacy path resolves to its target route with `redirectTo` set to the canonical hash path.
export function parseHash(hash) {
  const { path, query } = splitHash(hash);
  for (const [re, map] of REDIRECTS) {
    const m = re.exec(path);
    const target = m ? map(m, query) : null;
    if (target) return { ...parseHash(target), redirectTo: target };
  }
  for (const [name, re, keys] of ROUTES) {
    const m = re.exec(path);
    if (!m) continue;
    const params = {};
    keys.forEach((key, i) => { params[key] = decode(m[i + 1]); });
    if (name === 'workspace') params.clientId = query.client || '';
    if (name === 'client') {
      params.monthNumber = validMonth(params.month);
      delete params.month;
    }
    return { name, params, query, path };
  }
  return { name: 'notFound', params: {}, query, path };
}

let currentHash = hasWindow ? window.location.hash : '';
let current = parseHash(currentHash);
const listeners = new Set();

function applyRedirect() {
  if (!current.redirectTo) return;
  const target = `#${current.redirectTo}`;
  try {
    window.history.replaceState(window.history.state, '', target);
  } catch {
    // file:// or sandboxed frames may refuse replaceState; the parsed route is already the target.
  }
  currentHash = window.location.hash;
  current = { ...current, redirectTo: undefined };
}

function sync() {
  const hash = window.location.hash;
  if (hash === currentHash) return;
  currentHash = hash;
  current = parseHash(hash);
  applyRedirect();
  for (const fn of listeners) fn(current);
}

// ---------- dialog history entries ----------
// An open drawer/modal pushes a same-URL history entry, so the phone Back gesture closes the dialog (through its
// dirty check) instead of leaving the page. Closing a dialog pops its entry again; navigations and new entries
// requested while that pop is in flight wait for it, so they never land underneath it.
let popping = null; // { href, queue } while our own history.back() is in flight
let popTimer = 0;
const backListeners = new Set();
const liveDialogs = new Set(); // markers pushed by dialogs still mounted in this page session

export const dialogEntry = () => (hasWindow ? window.history.state?.frazierDialog : undefined);

function afterPop(fn) {
  if (popping) popping.queue.push(fn);
  else fn();
}

function settlePop() {
  clearTimeout(popTimer);
  const done = popping;
  popping = null;
  if (!done) return;
  // A dialog that rewrote the URL before closing (e.g. navigate replace) keeps that URL on the entry below.
  if (window.location.href !== done.href) {
    try { window.history.replaceState(window.history.state, '', done.href); } catch { /* sandboxed frame */ }
  }
  sync();
  done.queue.forEach((fn) => fn());
}

// Pushes an entry marked `marker` (a string). → false when the host refuses history writes.
export function pushDialogEntry(marker) {
  if (!hasWindow) return false;
  liveDialogs.add(marker);
  const push = () => {
    try {
      window.history.pushState({ ...(window.history.state || {}), frazierDialog: marker }, '', window.location.href);
      return true;
    } catch {
      return false;
    }
  };
  if (!popping) return push();
  popping.queue.push(push);
  return true;
}

// Removes the current entry when it is `marker`'s (the dialog closed some other way than Back).
export function popDialogEntry(marker) {
  liveDialogs.delete(marker);
  if (popping) return afterPop(() => popDialogEntry(marker));
  if (!hasWindow || dialogEntry() !== marker) return;
  popping = { href: window.location.href, queue: [] };
  popTimer = setTimeout(settlePop, 1000);
  try { window.history.back(); } catch { settlePop(); }
}

// fn() runs when the user moves back/forward through history (not for our own pops).
export function onHistoryBack(fn) {
  backListeners.add(fn);
  return () => backListeners.delete(fn);
}

function onPopState() {
  if (popping) return settlePop();
  for (const fn of backListeners) fn();
}

if (hasWindow) {
  applyRedirect();
  window.addEventListener('hashchange', () => { if (!popping) sync(); });
  window.addEventListener('popstate', onPopState);
}

export function getRoute() {
  return current;
}

export function useRoute() {
  const [route, setRoute] = useState(current);
  useEffect(() => {
    listeners.add(setRoute);
    if (route !== current) setRoute(current);
    return () => listeners.delete(setRoute);
  }, []);
  return route;
}

export function href(path) {
  const p = String(path || '/');
  return p.startsWith('#') ? p : `#${p.startsWith('/') ? p : `/${p}`}`;
}

// navigate('/workspace?client=abc') pushes a history entry; { replace: true } rewrites the current one.
export function navigate(path, { replace = false } = {}) {
  if (popping) return afterPop(() => navigate(path, { replace }));
  const target = href(path);
  // Leaving from an open dialog's entry: the new page takes that entry's place, so Back returns to the page below.
  const fromDialog = !replace && liveDialogs.has(dialogEntry()) && target !== window.location.hash;
  if (replace || fromDialog) {
    try {
      const { frazierDialog, ...rest } = window.history.state || {};
      window.history.replaceState(fromDialog ? rest : window.history.state, '', target);
    } catch {
      window.location.replace(target);
      return;
    }
  } else if (target !== window.location.hash) {
    window.location.hash = target.slice(1);
  }
  sync();
}

// Merge `patch` into the current query; '' / null / undefined remove a key. Replaces history.
export function setQuery(patch) {
  const route = parseHash(window.location.hash);
  const query = { ...route.query };
  for (const [key, value] of Object.entries(patch || {})) {
    if (value == null || value === '') delete query[key];
    else query[key] = String(value);
  }
  navigate(buildPath(route.path, query), { replace: true });
}

// ---------- canonical links ----------

// Client workspace: '#/clients/abc' | '#/clients/abc/months/9' (+ ?view=months for the timeline).
export const clientHref = (clientId, month, { view } = {}) => {
  const n = validMonth(month);
  return href(buildPath(`/clients/${encodeURIComponent(clientId)}${n ? `/months/${n}` : ''}`, { view }));
};
export const clientsHref = ({ status } = {}) => href(buildPath('/clients', { status: status && status !== 'all' ? status : '' }));
// The Workspace keeps a Client filter; a client + month means the client workspace (v2.1 links keep working).
export const workspaceHref = ({ client, month, filter, q } = {}) => (client && month
  ? clientHref(client, month)
  : href(buildPath('/workspace', { filter: filter && filter !== 'all' ? filter : '', client, q })));
export const batchHref = (batchId) => href(`/batches/${encodeURIComponent(batchId)}`);
export const personHref = (memberId) => href(`/people/${encodeURIComponent(memberId)}`);

// ---------- breadcrumb & titles ----------

export const PAGE_TITLES = {
  overview: 'Overview',
  workspace: 'Workspace',
  clients: 'Clients',
  client: 'Client',
  batch: 'Batch',
  calendar: 'Calendar',
  performance: 'Performance',
  people: 'People',
  person: 'People',
  settings: 'Settings',
  notFound: 'Page not found',
};

// Breadcrumb for the TopBar → [{ label, href? }]; the last item is the current page (no href).
// Clients / <Client> / Month 09 / Batch 06. Names come from the store (`index` may be null while loading).
export function crumbsFor(route, index) {
  const items = [{ label: 'Clients', href: clientsHref() }];
  const clientItems = (client, monthNumber) => {
    if (!client) return;
    items.push({ label: client.name, href: clientHref(client.id) });
    if (monthNumber) items.push({ label: monthLabel(monthNumber), href: clientHref(client.id, monthNumber) });
  };
  switch (route.name) {
    case 'clients':
      break;
    case 'client': {
      const client = index?.clientsById?.get(route.params.clientId);
      if (client) clientItems(client, route.params.monthNumber);
      else items.push({ label: index ? 'Client not found' : '…' });
      break;
    }
    case 'batch': {
      const batch = index?.batchesById?.get(route.params.batchId);
      if (!batch) {
        items.push({ label: index ? 'Batch not found' : '…' });
        break;
      }
      const client = index.clientsById.get(batch.clientId);
      const month = index.monthsById.get(batch.monthId);
      clientItems(client, month?.monthNumber);
      items.push({ label: batchLabel(batch.batchNumber), title: batch.name });
      break;
    }
    case 'person': {
      items.length = 0;
      items.push({ label: 'People', href: href('/people') });
      const member = index?.teamById?.get(route.params.memberId);
      items.push({ label: member ? member.name : index ? 'Person not found' : '…' });
      break;
    }
    default:
      items.length = 0;
      items.push({ label: PAGE_TITLES[route.name] || PAGE_TITLES.notFound });
  }
  const last = items[items.length - 1];
  if (last) delete last.href;
  return items;
}
